// src/components/Tooltip.tsx
// Label for controls that show only an icon. Rendered in a body portal with
// fixed positioning so it is never clipped by a scrolling ancestor, and it
// flips to the other side of the trigger when the preferred side has no room.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { hiddenUntilPositioned, useAnchoredPosition } from './useAnchoredPosition';

/** Hover dwell before showing, so sweeping across a toolbar stays quiet. */
const DELAY_MS = 250;

export interface TooltipProps {
  /** Text shown in the tooltip; also the trigger's accessible name. */
  label: string;
  /** `left` / `right` are what a vertical strip of icons needs. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  children: ReactNode;
  className?: string;
  /** Interactive reference card; preserves the same portal positioning. */
  content?: ReactNode;
  onOpen?: () => void;
}

export function Tooltip({ label, side = 'top', children, className, content, onOpen }: TooltipProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const interactive = content !== undefined;
  const suppressFocus = useRef(false);

  const style = useAnchoredPosition(wrapRef, open, tipRef, {
    side,
    align: 'center',
    gap: 6,
    margin: 4,
  });

  const show = useCallback((immediate = false) => {
    clearTimeout(timer.current);
    clearTimeout(closeTimer.current);
    onOpen?.();
    if (immediate) setOpen(true);
    else timer.current = setTimeout(() => setOpen(true), DELAY_MS);
  }, [onOpen]);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    if (interactive) closeTimer.current = setTimeout(() => setOpen(false), 150);
    else setOpen(false);
  }, [interactive]);

  useEffect(() => () => { clearTimeout(timer.current); clearTimeout(closeTimer.current); }, []);
  useEffect(() => {
    if (!open || !interactive) return;
    const dismiss = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); event.stopPropagation(); suppressFocus.current = true; wrapRef.current?.querySelector<HTMLElement>('a,button')?.focus(); queueMicrotask(() => { suppressFocus.current = false; }); } };
    const outside = (event: PointerEvent) => { if (!wrapRef.current?.contains(event.target as Node) && !tipRef.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('keydown', dismiss);
    document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('keydown', dismiss); document.removeEventListener('pointerdown', outside); };
  }, [open, interactive]);

  return (
    <span
      ref={wrapRef}
      role={interactive ? "group" : undefined}
      className={cn('inline-flex', className)}
      onMouseEnter={() => show()}
      onMouseLeave={hide}
      onFocus={() => { if (!suppressFocus.current) show(true); }}
      onBlur={(event) => { if (!tipRef.current?.contains(event.relatedTarget as Node) && !event.currentTarget.contains(event.relatedTarget as Node)) hide(); }}
      onKeyDown={(event) => {
        if (interactive && event.key === 'ArrowDown') {
          event.preventDefault(); show(true);
          requestAnimationFrame(() => tipRef.current?.querySelector<HTMLElement>('a,button')?.focus());
        }
      }}
      // Acting on the trigger dismisses the label — it shouldn't linger over
      // whatever the click just opened.
      onPointerDown={interactive ? undefined : hide}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role={interactive ? 'dialog' : 'tooltip'}
            aria-label={interactive ? label : undefined}
            style={style ?? hiddenUntilPositioned}
            onMouseEnter={interactive ? () => clearTimeout(closeTimer.current) : undefined}
            onMouseLeave={interactive ? hide : undefined}
            onFocus={interactive ? () => clearTimeout(closeTimer.current) : undefined}
            onBlur={interactive ? (event) => { if (!event.currentTarget.contains(event.relatedTarget as Node) && !wrapRef.current?.contains(event.relatedTarget as Node)) hide(); } : undefined}
            onKeyDown={interactive ? (event) => { if (event.key !== 'Escape') event.stopPropagation(); } : undefined}
            onClick={interactive ? (event) => event.stopPropagation() : undefined}
            className={interactive ? 'z-[70] w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-lg' : 'pointer-events-none z-[60] whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md'}
          >
            {content ?? label}
          </div>,
          document.body,
        )}
    </span>
  );
}
